import { useSelector } from 'react-redux';
import {
  CurrencyExchange,
  InvestmentClassEnum,
  PaymentComponentEnum,
} from 'src/generated/graphql-types';
import store from 'src/redux/config/configureStore';
import { RootState } from 'src/redux/config/rootReducer';
import {
  CurrencyAmount,
  Entity,
  EntityStatusEnum,
  Investment,
  OverviewTotals,
  StatusCategoryFilterEnum,
} from 'src/types.d';
import { getOrganizationDetails } from 'src/utils/functions/investmentServices';
import { displayNumberFormat } from 'src/utils/functions/numberServices';
import { currencyDict } from 'src/utils/variables';
import {
  getCurrency,
  getEntityTotalInvestment,
  getInvestmentTotalDistribution,
  initCurrencyArr,
  isInvestmentStatusCCancelled,
  isInvestmentStatusCompleted,
  isInvestmentStatusPending,
  isInvestmentStatusStarted,
  mergeHoldings,
} from './investorServices';

/**
 * Converts a given amount to a given currency
 * @param values Array of values to convert
 * @param allCurrencyRates All currency rates from the redux store
 * @param investmentCurrency Currency of the investment
 * @param currency Currency to convert to
 * @returns Converted amounts
 */
function convertArrayOfValues(
  values,
  allCurrencyRates: CurrencyExchange[],
  investmentCurrency: string,
  currency: string,
) {
  //convert manual holdings and holdings
  return values?.map(item => {
    //convert the holding amount
    const convertedAmount = convert(
      item.value,
      item.date,
      allCurrencyRates,
      investmentCurrency,
      currency,
    );

    //return the converted holding
    return { date: item.date, value: convertedAmount };
  });
}

/**
 * Finds the nearest date to the requested date from the DB with a max of 30 days
 * @param requestedDate Requested date that could not be found
 * @param dbRates All dates from DB
 * @returns Nearest date to the requested date
 */
function findNearestDate(requestedDate: string, dbRates: any[]) {
  // add a day to the requested date
  const prevDate = new Date(requestedDate);

  //set max loop
  let maxLoop = 30;

  //try to find the next date
  while (maxLoop-- > 0) {
    //reduce one day from the prevDate
    prevDate.setDate(prevDate.getDate() - 1);

    //get the next date string
    const prevDateStr = prevDate.toISOString().split('T')[0];

    //try to find the next date from db
    const foundDate = dbRates?.find(dbRate => dbRate.date === prevDateStr);

    //if found return the last known exchange rate
    if (!foundDate) continue;

    //if found return the last known exchange rate
    return { date: prevDateStr, currencyRates: foundDate.currencyRates };
  }

  //if no rates found return null
  return null;
}

/**
 * Convert a given amount to a given currency
 * @param amount Given amount to convert
 * @param date Date of the amount
 * @param allCurrencyRates All currency rates from the redux store
 * @param fromCurrency Currency to convert from
 * @param toCurrency Currency to convert to
 * @returns Converted amount
 */
function convert(
  amount: number,
  date: Date,
  allCurrencyRates: CurrencyExchange[],
  fromCurrency: string,
  toCurrency: string,
) {
  //return the amount if no currency exchange is needed
  if (fromCurrency === toCurrency) return amount;

  //validate date is not null and not Invalid Date
  if (!date || !Date.parse(date.toString())) return amount;

  //get the date in the db format YYYY-MM-DD
  const dateDbFormat = new Date(date).toISOString().split('T')[0];

  //find the currency rate for the conversion date
  let filteredRates = allCurrencyRates?.find(rate => rate.date === dateDbFormat);

  //if no rate found - try to get the nearest rates by date
  if (!filteredRates) {
    const nearRate = findNearestDate(dateDbFormat, allCurrencyRates);

    //could not find the nearest date - return the original holding
    if (!nearRate) {
      console.error('could not find the nearest date for: ', date);
      return amount;
    }

    filteredRates = nearRate;
  }

  //if no rate found - return the original holding
  if (!filteredRates) return amount;

  //get the rate
  const rate = filteredRates?.currencyRates[`${fromCurrency}_${toCurrency}`];

  //convert the holding amount
  const convertedAmount = amount * rate;

  //return the converted holding
  return convertedAmount;
}

/**
 * Get the rate for a given currency and date for investment v2
 * @param dbRate Given rate from the DB
 * @param date Date from the DB record
 * @param fromCurrency Currency to convert from
 * @param fromCurrency Currency to convert to
 * @param rates All rates
 * @param recursiveFlip Should try to flip the currency
 * @returns Rate for the given currency and date
 */
function getV2Rate(
  dbRate: number,
  date: string,
  fromCurrency: string,
  toCurrency: string,
  rates: CurrencyExchange[],
  recursiveFlip: boolean = false,
): number {
  // //1. rate exist on db - return it
  // if (dbRate) return dbRate;

  //2. currency is USD - return 1
  if (fromCurrency === toCurrency) return 1;

  //3. currency is not USD - get the rate from the rates collection
  //get the date in the db format YYYY-MM-DD
  const dateDbFormat = new Date(date).toISOString().split('T')[0];
  const currencyPairSymbol = `${fromCurrency}_${toCurrency}`;

  //return the rate from the rates collection
  const foundRate = rates?.find(rate => rate.date === dateDbFormat);

  //find the rate directly
  let rate = foundRate?.currencyRates[currencyPairSymbol];
  if (rate) return rate;

  //4. if no rate found - try to get the nearest rates by date
  const nearRate = findNearestDate(dateDbFormat, rates);
  rate = nearRate?.currencyRates[currencyPairSymbol];
  if (rate) return rate;

  //if the recursive flip is true - stop trying and return 1
  if (recursiveFlip) {
    console.error('could not find the nearest date for: ', date);
    return null;
  }

  //5. try to flip the symbol
  const flippedValue = getV2Rate(dbRate, date, toCurrency, fromCurrency, rates, true);
  if (!flippedValue) return 1; //recursive didn't worked
  return 1 / flippedValue;
}

/**
 * Convert an investment to a given currency for version 2
 * @param investment Given investment
 * @param toCurrency Currency to convert to
 * @param rates All rates
 * @returns Converted investment
 */
function convertInvestmentV2(
  investment: Investment,
  toCurrency: string,
  rates: CurrencyExchange[],
): Investment {
  //Version 2 Sum up all fields
  //convert distributions
  const investmentDistributionsV2 = investment.distributions?.map(item => {
    const amount =
      item?.amount * getV2Rate(item?.currencyRate, item?.date, item?.currency, toCurrency, rates);
    return { ...item, amount };
  });

  //convert contributions
  //1. contribution
  let investmentContributionsV2 = investment.contributions?.map(item => {
    const contribution =
      item?.contribution *
      getV2Rate(item?.currencyRate, item?.date, item?.currency, toCurrency, rates);
    return { ...item, contribution };
  });

  //2. commitments
  investmentContributionsV2 = investmentContributionsV2?.map(item => {
    const commitment =
      item?.commitment *
      getV2Rate(item?.currencyRate, item?.date, item?.currency, toCurrency, rates);
    return { ...item, commitment };
  });

  //3. capital calls
  investmentContributionsV2 = investmentContributionsV2?.map(item => {
    const capitalCall =
      item?.capitalCall *
      getV2Rate(item?.currencyRate, item?.date, item?.currency, toCurrency, rates);
    return { ...item, capitalCall };
  });

  //return the converted investment
  return {
    ...investment,
    distributions: investmentDistributionsV2,
    contributions: investmentContributionsV2,
  };
}

/**
 * Convert an investment to a given currency for version 1
 * @param investment Given investment
 * @param toCurrency Currency to convert to
 * @param rates All rates
 * @returns Converted investment
 */
function convertInvestmentV1(
  investment: Investment,
  toCurrency: string,
  rates: CurrencyExchange[],
): Investment {
  //get the investment currency
  const investmentCurrency = getCurrency(investment);

  //on commitment - set the investment date for today
  const convertedCommitment = convert(
    investment?.commitment,
    investment?.date,
    rates,
    investmentCurrency,
    toCurrency,
  );

  //convert the holding amount
  const convertedManualHoldings = convertArrayOfValues(
    investment?.manualHoldings,
    rates,
    investmentCurrency,
    toCurrency,
  );
  const convertedHoldings = convertArrayOfValues(
    investment?.holdings,
    rates,
    investmentCurrency,
    toCurrency,
  );

  //convert investment amount (contributions)
  const convertedInvestmentAmount = convertArrayOfValues(
    investment?.investmentAmount,
    rates,
    investmentCurrency,
    toCurrency,
  );

  //return the converted investment
  return {
    ...investment,
    commitment: convertedCommitment,
    manualHoldings: convertedManualHoldings,
    holdings: convertedHoldings,
    investmentAmount: convertedInvestmentAmount,
    currency: toCurrency,
  };
}

/**
 * Converts all investments values to a given currency - support both v1 and v2 investments
 * @param investments Given investments
 * @param toCurrency Currency to convert to
 * @returns Converted investments
 */
export function convertInvestmentsToCurrency(
  investments: Investment[],
  toCurrency: string,
): Investment[] {
  //validate input
  if (!investments || investments?.length === 0 || !toCurrency) return investments;

  //get the currency rates from the redux store
  const { allCurrencyRates } = store.getState().general;

  //loop over the investments and convert the currency
  const convertedInvestments = investments?.map(investment => {
    //convert v1 parts
    let convertedInvestment: Investment = convertInvestmentV1(
      investment,
      toCurrency,
      allCurrencyRates,
    );
    //convert v2 parts
    convertedInvestment = convertInvestmentV2(convertedInvestment, toCurrency, allCurrencyRates);

    //return the converted investment
    return convertedInvestment;
  });

  //return the converted investments
  return convertedInvestments;
}

/**
 * Gets the overview remaining commitments value
 * @param investments Given investments
 * @param convertToUSD Should convert to USD
 * @returns  remaining commitments
 */
export function getOverviewCommitments(
  investments: Investment[],
  convertToUSD: boolean,
): {
  remaining: number;
  total: number;
} {
  //init distribution,total commitments, remaining commitments, active contributions and total contributions
  let totalCommitments: number = 0;
  let totalRemainingCommitments: number = 0;
  let activeContributions: number = 0;
  let totalContributions: number = 0;

  //set currency and symbol
  const overviewCurrency =
    investments && investments.length > 0 ? getCurrency(investments[0]) : 'USD';

  //loop over the investments and sum up the totals
  if (investments)
    investments.forEach(singleInv => {
      //Version 0 Sum up all fields
      const investmentContributionsV0 = getEntityTotalInvestment(singleInv.investmentAmount);

      //Version 2 Sum up all fields
      const investmentContributionsV2 =
        singleInv.contributions?.reduce((sum, item) => sum + item.contribution, 0) ?? 0;
      const investmentCommitments =
        singleInv.contributions?.reduce((sum, item) => sum + item.commitment, 0) ?? 0;

      //sum up both versions
      const investmentContributions = investmentContributionsV0 + investmentContributionsV2;

      totalCommitments += investmentCommitments;

      //Add to on going investments only if for status started
      if (isInvestmentStatusStarted(singleInv)) activeContributions += investmentContributions;

      //Add all investments without pending
      if (!isInvestmentStatusPending(singleInv)) {
        totalContributions += investmentContributions;
      }

      //get the current currency
      const currentCurrency: string = getCurrency(singleInv);

      //calc the remaining commitments
      let remainingCommitments = investmentCommitments - investmentContributions;

      //if the investment is cancelled - set the remaining to 0
      if (isInvestmentStatusCCancelled(singleInv) || isInvestmentStatusCompleted(singleInv))
        remainingCommitments = 0;

      //convert the remaining commitments to USD with today's rates
      //Note: The commitments are converted with today's rate since as long as the funds are not payed - the current rate applies
      remainingCommitments = convertToUSD
        ? convertAmountToUSD(remainingCommitments, currentCurrency)
        : remainingCommitments;
      totalRemainingCommitments += remainingCommitments > 0 ? remainingCommitments : 0; //add 0 instead of negative
    });

  return {
    remaining: totalRemainingCommitments,
    total: totalContributions + totalRemainingCommitments,
  };
}

/**
 * Gets the totals for all investments
 * @param investments Given investments
 * @returns Distributions, total commitments, remaining commitments, active contributions and total contributions
 */
export function getOverviewTotals(investments: Investment[]): OverviewTotals {
  //init distribution,total commitments, remaining commitments, active contributions and total contributions
  let totalDistributions: number = 0;
  let totalCommitments: number = 0;
  let totalRemainingCommitmentsConverted: number = 0;
  let activeContributions: number = 0;
  let totalContributions: number = 0;
  let totalPrincipalReturns: number = 0; //All principals return from overall distributions
  let totalEquityDistributions: number = 0; //All equity distributions

  //set currency and symbol
  const overviewCurrency =
    investments && investments.length > 0 ? getCurrency(investments[0]) : 'USD';
  const overviewCurrencySymbol = getCurrencySymbol(overviewCurrency);

  //loop over the investments and sum up the totals
  if (investments)
    investments.forEach(singleInv => {
      //Version 2 Sum up all fields
      const investmentContributions =
        singleInv?.contributions?.reduce((sum, item) => sum + item.contribution, 0) ?? 0;
      const investmentDistributions =
        singleInv?.distributions?.reduce((sum, item) => sum + item.amount, 0) ?? 0;
      const investmentCommitments =
        singleInv?.contributions?.reduce((sum, item) => sum + item.commitment, 0) ?? 0;

      //sum up all principals in distributions
      const investmentPrincipalReturns =
        singleInv?.distributions?.reduce(
          (sum, item) =>
            item.paymentComponent === PaymentComponentEnum.Principal ? sum + item.amount : sum,
          0,
        ) ?? 0;

      //sum up all equity distributions
      const investmentEquityDistributions =
        singleInv?.distributions?.reduce(
          (sum, item) =>
            item.investmentClassType === InvestmentClassEnum.Equity ? sum + item.amount : sum,
          0,
        ) ?? 0;

      //sum totals
      totalDistributions += investmentDistributions;
      totalCommitments += investmentCommitments;
      totalEquityDistributions += investmentEquityDistributions;

      //Add to on going investments only if for status started
      if (isInvestmentStatusStarted(singleInv)) activeContributions += investmentContributions;

      //if investment is not active - set the invested capital to 0  by matching the investment contributions
      totalPrincipalReturns += !isInvestmentStatusStarted(singleInv)
        ? investmentContributions
        : investmentPrincipalReturns;

      //Add all investments without pending
      if (!isInvestmentStatusPending(singleInv)) {
        totalContributions += investmentContributions;
      }

      //calc the remaining commitments
      const remainingCommitments = investmentCommitments - investmentContributions;
      const isActiveEntity: boolean = isInvestmentStatusStarted(singleInv); //Check if the entity is active
      totalRemainingCommitmentsConverted +=
        isActiveEntity && remainingCommitments > 0 ? remainingCommitments : 0; //add 0 instead of negative
    });

  return {
    totalDistributions,
    totalCommitments,
    totalRemainingCommitments: totalRemainingCommitmentsConverted, //to be overwritten later in the code
    totalPrincipalReturns,
    activeContributions,
    totalContributions,
    overviewCurrency,
    overviewCurrencySymbol,
    convertToUSD: false,
    totalRemainingCommitmentsConverted,
    totalAccumulatedInterest: 0, //to be overwritten later in the code
    totalEquityDistributions,
  };
}

/**
 * Create currency amount array from investments
 * @param investments Given investments
 * @param convert Should convert to USD
 * @returns Currency amount array
 */
export function createCurrencyAmountArrFromInvestments(
  investments: Investment[],
  convert: boolean = false,
) {
  //init arrays
  let distributions: CurrencyAmount[] = [];
  let commitments: CurrencyAmount[] = [];
  let remainingCommitments: CurrencyAmount[] = [];
  let onGoingInvestments: CurrencyAmount[] = [];
  let totalInvestments: CurrencyAmount[] = [];

  //return empty arrays on empty investments
  if (!investments)
    return {
      distributions,
      commitments,
      onGoingInvestments,
      totalInvestments,
      remainingCommitments,
    };

  //loop over the investments and create CurrencyAmount object for each investment type
  investments.forEach(singleInv => {
    const investmentCurrency = getCurrency(singleInv);
    const currency: string = !convert ? investmentCurrency : 'USD';

    //if should convert and currency is not USD - convert to USD
    const rate = convert ? convertAmountToUSD(1, investmentCurrency) : 1;

    //Set 0 for new currencies
    initCurrencyArr(currency, distributions);
    initCurrencyArr(currency, commitments);
    initCurrencyArr(currency, onGoingInvestments);
    initCurrencyArr(currency, totalInvestments);
    initCurrencyArr(currency, remainingCommitments);

    //Version 0 Sum up all fields
    const investmentContributionsV0 = getEntityTotalInvestment(singleInv.investmentAmount);

    //merge the holdings collection
    const mergedHolding = mergeHoldings(singleInv.manualHoldings, singleInv.holdings);

    const investmentDistributionsV0 = getInvestmentTotalDistribution(mergedHolding, rate);

    //Version 2 Sum up all fields
    const investmentContributionsV2 =
      singleInv.contributions?.reduce((sum, item) => sum + item.contribution * rate, 0) ?? 0;
    const investmentDistributionsV2 =
      singleInv.distributions?.reduce((sum, item) => sum + item.amount * rate, 0) ?? 0;
    const investmentCommitments =
      singleInv.contributions?.reduce((sum, item) => sum + item.commitment * rate, 0) ?? 0;

    //sum up both versions
    const investmentContributions = investmentContributionsV0 + investmentContributionsV2;
    const investmentDistributions = investmentDistributionsV0 + investmentDistributionsV2;

    distributions[currency] += investmentDistributions;
    commitments[currency] += investmentCommitments;

    //Add to on going investments only if for status started
    if (isInvestmentStatusStarted(singleInv))
      onGoingInvestments[currency] += investmentContributions;

    //Add all investments without pending
    if (!isInvestmentStatusPending(singleInv)) {
      totalInvestments[currency] += investmentContributions;
    }

    //calc the remaining commitments
    const remainCommitment = investmentCommitments - investmentContributions;
    remainingCommitments[currency] += remainCommitment > 0 ? remainCommitment : 0; //add 0 instead of negative
  });

  return { distributions, commitments, onGoingInvestments, totalInvestments, remainingCommitments };
}

//Gets the exchange rate from the redux store
export function useGetUSDExchangeRates() {
  return useSelector((state: RootState) => state.general).currencyRates;
}

//Gets the global currency Symbol
export function useGlobalCurrencySymbol() {
  return useSelector((state: RootState) => state.investor.overviewTotals.overviewCurrencySymbol);
}

//Apply USD Exchange rate to a given currency amounts array
//Returns a single USD value for the array
export function convertToUSDByRate(currencyAmounts: CurrencyAmount[]): number {
  //gets the array keys - currencies
  const currKeys = Object.keys(currencyAmounts);

  //skip empty arrays
  if (currKeys.length === 0) return 0;

  //init usd amount
  let usdAmount = 0;

  //loop over each currency pair and add to the total USD value
  currKeys.forEach(keyCurrency => {
    //get the rate add to the total amount
    usdAmount += convertAmountToUSD(currencyAmounts[keyCurrency], keyCurrency); // rate should be 1 for USD
  });

  //return an array with USD only
  return usdAmount;
}

//Convert a single amount to USD
export function convertAmountToUSD(amount: number, baseSymbol: string): number {
  //do not convert USD
  if (baseSymbol === 'USD') return amount;

  //get exchange rate from redux store
  const { currencyRates } = store.getState().general;

  if (!currencyRates) return 0;

  //set the symbol - ex. EUR_USD
  const keyRateSymbol = `${baseSymbol}_USD`;

  //Skip unknown currencies
  if (!(keyRateSymbol in currencyRates)) {
    console.error('Error - Could not find currency pair for: ', keyRateSymbol);
    return 0;
  }

  //get the rate and add to the total amount
  const rate = currencyRates[keyRateSymbol];

  return amount * rate; // rate should be 1 for USD
}

/**
 * Gets the converted aggregated investment for a given project
 * @param investment Given aggregated investment
 * @returns Converted aggregated investment
 */
export function getProjectConvertedInvestment(investment: Investment): Investment {
  //get base currency for the project
  const currency = getCurrencyByInvestment(investment);

  //update the converted investment
  const converted = convertInvestmentsToCurrency([investment], currency);

  //return the converted investment
  return converted && converted.length > 0 ? converted[0] : null;
}

export function checkIfCancelled(investment: Investment): boolean {
  const { categoryFilter } = store.getState().investor;

  //finding the investments entity
  const status = investment?.projectDetails?.status || investment?.fundDetails?.status;
  return (
    categoryFilter === StatusCategoryFilterEnum.CANCELLED || status !== EntityStatusEnum.CANCELLED
  );
}

/**
 * Gets the overview currency and investments to USD
 * @param investments Given Investments
 * @returns Overview currency, convert to USD and overview currency symbol
 */
export function setOverviewCurrencyAndInvestments(investments: Investment[]) {
  //loop over all investments and get the currency
  const currencies =
    investments.map(investment => getCurrencyByInvestment(investment)).filter(exist => exist) ?? [];

  //make currencies unique
  const uniqueCurrencies = [...new Set(currencies)];

  //if there is only one currency - set it as the overview currency
  const overviewCurrency = uniqueCurrencies.length === 1 ? uniqueCurrencies[0] : 'USD';

  //set whether to convert to USD
  const convertToUSD = uniqueCurrencies.length > 1 && overviewCurrency === 'USD';

  //set the overview currency symbol
  const overviewCurrencySymbol = getCurrencySymbol(overviewCurrency);

  //update the converted investment
  const convertedInvestments = convertToUSD
    ? convertInvestmentsToCurrency(investments, 'USD')
    : investments.map(investment => ({ ...investment, currency: overviewCurrency }));

  return { overviewCurrency, convertToUSD, overviewCurrencySymbol, convertedInvestments };
}

//Get the number of currencies found and the main currency to be used
// USD is the default for more than 1 currency
export function getGlobalCurrencyFromCurrencyAmounts(currencyAmounts): {
  convertToUSD: boolean;
  overviewCurrency: string;
} {
  //remove keys with empty values
  removeEmptyKeys(currencyAmounts);

  //set main currency to USD by default
  let overviewCurrency = 'USD';

  //Get the number of currencies found within all
  const numOfCurrencies = Object.keys(currencyAmounts).length;

  // if only one currency exist set it as the main currency
  // if there was more than 1 currency USD is the default
  if (numOfCurrencies === 1) overviewCurrency = Object.keys(currencyAmounts)[0];

  //Convert currencies only if there is more than 1 currency involved
  const convertToUSD = numOfCurrencies > 1;

  // If there are more than a single currency set USD as the default
  return { convertToUSD, overviewCurrency };
}

//Returns an array with formatted values to an array
export function getSignedCurrenciesArray(currencyAmountsObj: CurrencyAmount[]) {
  //remove keys with no meaningful value
  removeEmptyKeys(currencyAmountsObj);

  let arr = [];
  const objKeys = Object.keys(currencyAmountsObj);

  //return 0 in case no value exists
  if (objKeys.length === 0) return [0];

  // Loop over the existing object - 'Currency': Amount (for example 'USD': 10000)
  // Create new array with formatted 'currencySign number (for example $10K)
  objKeys.forEach(key => {
    arr.push(displayNumberFormat(currencyAmountsObj[key], currencyDict[key]));
  });

  return arr;
}

// Removes all keys with 0 or invalid numbers
export function removeEmptyKeys(obj: CurrencyAmount[]) {
  const allKeys = Object.keys(obj);
  allKeys.forEach(key => {
    if (obj[key] === 0 || Number.isNaN(obj[key])) delete obj[key];
  });
}

/// ------------ Get currency symbol functions per data types --------------------------------------

//Gets the currency symbol by entity
export function getCurrencyByEntity(entity: Entity): string {
  const currency = entity.currency || 'USD';
  return getCurrencySymbol(currency);
}

//Gets the currency by investment
export function getCurrencyByInvestment(investment: Investment): string {
  const entity = getOrganizationDetails(investment);

  //get the entity currency if exist
  const currency = entity?.currency ? entity.currency : entity.currency;
  return currency ?? 'USD';
}

//Get the currency symbol by investment
export function getCurrencySymbolByInvestment(investment: Investment): string {
  const currency = getCurrencyByInvestment(investment);
  return getCurrencySymbol(currency);
}

//Gets the Symbol of the currency
export function getCurrencySymbol(currency: string | undefined): string {
  return currency ? currencyDict[currency?.toUpperCase()] : '$';
}
